import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"
import { getDeviceFingerprint, preloadFingerprint } from "~/lib/device_fingerprint"

// Type declarations for Cloudflare Turnstile and Umami analytics
declare global {
  interface Window {
    turnstile?: {
      reset: (widgetId: string | HTMLElement) => void;
      render: (element: string | HTMLElement, options: object) => string;
      remove: (widgetId: string) => void;
      getResponse: (widgetId: string) => string | null;
    };
    umami?: {
      track: (event: string, data?: Record<string, unknown>) => void;
    };
  }
}

// State machine for the arena controller:
//
// State        | Allowed user actions          | Transitions to
// -------------|-------------------------------|---------------------------------------------
// loading      | (none)                        | ready (after rAF)
// ready        | vote (←/→/T/click), skip (⎵)  | submitting (vote), loading (skip)
// submitting   | queue continue (⎵)            | revealing (POST ok), loading (pendingContinue), ready (error)
// revealing    | continue (⎵/click)            | loading (continue/countdown expires)
//
type ArenaState = "loading" | "ready" | "submitting" | "revealing"

export default class ArenaController extends Controller {
  static targets = ["matchup", "reveal", "leftCard", "rightCard", "leftId", "rightId", "competitionId", "pinContext", "countdown", "countdownValue", "progressBar", "turnstileContainer", "promptBox", "countdownOverlay", "leftVideo", "rightVideo", "votingCountdown", "votingCountdownValue", "videoMode", "revealVideo"]
  static values = {
    autoAdvance: { type: Number, default: 3000 },
    featuring: { type: String, default: "" },
    challenge: { type: String, default: "" },
    battle: { type: String, default: "" },
    visitorMode: { type: Boolean, default: false },
    competitionTypeSlug: { type: String, default: "text-to-image" },
    videoMode: { type: Boolean, default: false },
    votingDelaySeconds: { type: Number, default: 5 }
  }

  declare matchupTarget: HTMLElement
  declare revealTarget: HTMLElement
  declare leftCardTarget: HTMLElement
  declare rightCardTarget: HTMLElement
  declare leftIdTarget: HTMLInputElement
  declare rightIdTarget: HTMLInputElement
  declare competitionIdTarget: HTMLInputElement
  declare countdownTarget: HTMLElement
  declare countdownValueTarget: HTMLElement
  declare progressBarTarget: HTMLElement
  declare turnstileContainerTarget: HTMLElement
  declare promptBoxTarget: HTMLElement
  declare autoAdvanceValue: number
  declare featuringValue: string
  declare challengeValue: string
  declare battleValue: string
  declare visitorModeValue: boolean
  declare competitionTypeSlugValue: string
  declare videoModeValue: boolean
  declare votingDelaySecondsValue: number

  declare hasMatchupTarget: boolean
  declare hasRevealTarget: boolean
  declare hasCountdownTarget: boolean
  declare hasTurnstileContainerTarget: boolean
  declare pinContextTarget: HTMLInputElement
  declare hasPromptBoxTarget: boolean
  declare countdownOverlayTarget: HTMLElement
  declare hasProgressBarTarget: boolean
  declare hasCompetitionIdTarget: boolean
  declare hasPinContextTarget: boolean
  declare hasCountdownOverlayTarget: boolean
  declare leftVideoTarget: HTMLVideoElement
  declare rightVideoTarget: HTMLVideoElement
  declare hasLeftVideoTarget: boolean
  declare hasRightVideoTarget: boolean
  declare votingCountdownTargets: HTMLElement[]
  declare votingCountdownValueTargets: HTMLElement[]
  declare videoModeTarget: HTMLInputElement
  declare hasVideoModeTarget: boolean
  declare revealVideoTargets: HTMLVideoElement[]

  private state: ArenaState = "ready"
  private pendingContinue = false
  private countdownRafId: number | null = null
  private countdownStartTime: number = 0
  private lastKeyPressTime = 0
  private static KEY_COOLDOWN_MS = 250 // Prevent rapid/held keys from firing multiple actions
  private prefetchedResponse: string | null = null
  private prefetchPromise: Promise<void> | null = null
  private votingEnabled = true
  private videoVotingRafId: number | null = null

  private boundHandleKeydown = this.handleKeydown.bind(this)
  private boundClearFeaturing = this.clearFeaturing.bind(this)
  private boundClearChallenge = this.clearChallenge.bind(this)
  private boundClearBattle = this.clearBattle.bind(this)

  connect() {
    document.addEventListener("keydown", this.boundHandleKeydown)
    window.addEventListener("arena:featuring-cleared", this.boundClearFeaturing)
    window.addEventListener("arena:challenge-cleared", this.boundClearChallenge)
    window.addEventListener("arena:battle-cleared", this.boundClearBattle)

    this.state = "ready"

    // Pre-load device fingerprint for faster first vote
    if (this.visitorModeValue) {
      preloadFingerprint()
    }

    // For video mode, check if we need to set up the voting delay
    this.checkVideoMode()
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleKeydown)
    window.removeEventListener("arena:featuring-cleared", this.boundClearFeaturing)
    window.removeEventListener("arena:challenge-cleared", this.boundClearChallenge)
    window.removeEventListener("arena:battle-cleared", this.boundClearBattle)
    this.stopCountdown()
    this.stopVideoVotingCountdown()
    this.prefetchedResponse = null
    this.prefetchPromise = null
  }

  clearFeaturing() {
    this.featuringValue = ""
  }

  clearChallenge() {
    this.challengeValue = ""
  }

  clearBattle() {
    this.battleValue = ""
  }

  // -- Input handling (dispatches on state) --

  handleKeydown(event: KeyboardEvent) {
    // Ignore if typing in an input field
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return
    }

    // List of keys we handle - ignore others entirely
    const actionKeys = ["ArrowLeft", "ArrowRight", "t", "T", " "]
    if (!actionKeys.includes(event.key)) return

    // Block ALL key repeat events (when any key is held down)
    if (event.repeat) return

    // Vote keys must short-circuit BEFORE the cooldown timestamp update —
    // otherwise mashing arrows during the video pre-vote countdown burns the
    // cooldown and the first valid press after the timer ends can land
    // inside the 250ms window and silently miss.
    const isVoteKey = event.key === "ArrowLeft" || event.key === "ArrowRight" || event.key === "t" || event.key === "T"
    if (isVoteKey && !this.canVote()) return

    // Apply cooldown to prevent rapid key presses from stacking up
    const now = Date.now()
    if (now - this.lastKeyPressTime < ArenaController.KEY_COOLDOWN_MS) return
    this.lastKeyPressTime = now

    switch (event.key) {
      case "ArrowLeft":
        this.voteLeft()
        break
      case "ArrowRight":
        this.voteRight()
        break
      case "t":
      case "T":
        this.voteTie()
        break
      case " ":
        event.preventDefault()
        if (this.state === "ready") this.skip()
        else if (this.state === "submitting") this.pendingContinue = true
        else if (this.state === "revealing") this.continueNow()
        // "loading" → ignored
        break
    }
  }

  // Single source of truth for "is the user allowed to cast a vote right now?"
  // Used by both keyboard and click paths to gate vote submission consistently.
  private canVote(): boolean {
    return this.state === "ready" && this.votingEnabled
  }

  // -- User actions --

  voteLeft(event?: Event) {
    event?.preventDefault()
    if (!this.canVote()) return
    this.submitVote("left_wins")
  }

  voteRight(event?: Event) {
    event?.preventDefault()
    if (!this.canVote()) return
    this.submitVote("right_wins")
  }

  voteTie(event?: Event) {
    event?.preventDefault()
    if (!this.canVote()) return
    this.submitVote("tie")
  }

  skip() {
    if (this.state !== "ready") return
    this.state = "loading"
    try { window.umami?.track("arena-skip", { competition_type: this.competitionTypeSlugValue }) } catch {}
    this.loadNextMatchup()
  }

  continueNow() {
    if (this.state !== "revealing") return
    this.state = "loading"
    this.stopCountdown()
    this.loadNextMatchup()
  }

  // -- Vote submission (ready → submitting → revealing | loading) --

  async submitVote(outcome: string) {
    if (this.state !== "ready") return

    // Capture IDs before transitioning state to prevent any race conditions
    const leftId = this.leftIdTarget?.value
    const rightId = this.rightIdTarget?.value

    // Guard against missing or empty IDs (could happen on double-submit)
    if (!leftId || !rightId) return

    this.state = "submitting"

    // Clear the hidden inputs immediately to prevent any possibility of re-use
    this.leftIdTarget.value = ""
    this.rightIdTarget.value = ""

    // Disable all interactions on the matchup area
    this.matchupTarget.style.pointerEvents = "none"
    this.matchupTarget.style.opacity = "0.7"

    const cardToAnimate = outcome === "left_wins" ? this.leftCardTarget :
                          outcome === "right_wins" ? this.rightCardTarget : null
    cardToAnimate?.classList.add("animate-pulse")

    // Build request body with optional Turnstile token and device fingerprint for visitors
    const bodyParams: Record<string, string> = {
      left_competitor_id: leftId,
      right_competitor_id: rightId,
      outcome
    }

    // Include pin context for tracking which pinning mode was active
    if (this.hasPinContextTarget && this.pinContextTarget.value) {
      bodyParams["pin_context"] = this.pinContextTarget.value
    }

    // Include Turnstile token for visitor verification
    const turnstileToken = this.getTurnstileToken()
    if (turnstileToken) {
      bodyParams["cf-turnstile-response"] = turnstileToken
    }

    // Include device fingerprint for secondary deduplication
    if (this.visitorModeValue) {
      const deviceFingerprint = await getDeviceFingerprint()
      if (deviceFingerprint) {
        bodyParams["device_fingerprint"] = deviceFingerprint
      }
    }

    try {
      const slug = this.competitionTypeSlugValue || "text-to-image"
      const response = await fetch(`/arena/vote/${slug}/battles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this.getCSRFToken()
        },
        body: new URLSearchParams(bodyParams)
      })

      if (response.ok) {
        try { window.umami?.track("arena-vote", { outcome, competition_type: this.competitionTypeSlugValue }) } catch {}
        Turbo.renderStreamMessage(await response.text())

        if (this.pendingContinue) {
          // User pressed space during submission — skip reveal, load next immediately
          this.pendingContinue = false
          this.state = "loading"
          this.loadNextMatchup()
        } else {
          this.state = "revealing"
          // Wait for Stimulus to observe the new DOM targets after Turbo update
          requestAnimationFrame(() => {
            this.startCountdown()
            this.scrollToPromptBox()
            this.playRevealVideos()
            this.prefetchNextMatchup()
            document.dispatchEvent(new CustomEvent("arena:content-updated"))
          })
        }
      } else if (response.status === 422) {
        // Duplicate vote (e.g. device fingerprint match from another session) —
        // silently skip to the next matchup instead of showing an error
        this.state = "loading"
        this.loadNextMatchup()
      } else {
        this.state = "ready"
      }
    } catch (error) {
      console.error("Vote failed:", error)
      this.state = "ready"
    }
  }

  // -- Countdown --

  startCountdown() {
    // Clear any existing countdown to prevent orphaned timers
    this.stopCountdown()

    const duration = this.autoAdvanceValue

    // Drive progress bar with CSS animation (compositor thread, iOS-reliable)
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.setProperty("--arena-countdown-duration", `${duration}ms`)
      this.progressBarTarget.classList.add("arena-progress-animated")
    }

    // Drive text countdown with requestAnimationFrame (battery-efficient)
    this.countdownStartTime = performance.now()

    const tick = (now: number) => {
      const elapsed = now - this.countdownStartTime
      const remaining = Math.ceil((duration - elapsed) / 1000)

      if (this.hasCountdownTarget && this.countdownValueTarget) {
        this.countdownValueTarget.textContent = String(Math.max(0, remaining))
      }

      if (elapsed >= duration) {
        this.countdownRafId = null
        this.state = "loading"
        this.loadNextMatchup()
        return
      }

      this.countdownRafId = requestAnimationFrame(tick)
    }

    this.countdownRafId = requestAnimationFrame(tick)
  }

  stopCountdown() {
    if (this.countdownRafId) {
      cancelAnimationFrame(this.countdownRafId)
      this.countdownRafId = null
    }

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.classList.remove("arena-progress-animated")
      this.progressBarTarget.style.width = "0%"
    }
  }

  // -- Load next matchup (only called when state is already "loading") --

  async loadNextMatchup() {
    this.stopCountdown()
    // Cancel any in-flight pre-vote (video Watch) countdown so its rAF tick
    // can't outlive the matchup it belongs to. Without this, skipping during
    // the 5s pre-vote window leaves an orphan tick that finishes on the OLD
    // startTime — shortening the new matchup's countdown (e.g. to ~2s).
    this.stopVideoVotingCountdown()
    this.pendingContinue = false

    try {
      // Wait for any in-flight prefetch to complete
      if (this.prefetchPromise && !this.prefetchedResponse) {
        await this.prefetchPromise
      }

      if (this.prefetchedResponse) {
        // Use prefetched response — images already cached by browser
        const prefetched = this.prefetchedResponse
        this.prefetchedResponse = null
        this.prefetchPromise = null
        Turbo.renderStreamMessage(prefetched)
        document.getElementById("arena_prefetch")?.replaceChildren()
      } else {
        // Fallback: fetch normally if no prefetched data
        this.prefetchedResponse = null
        this.prefetchPromise = null
        const response = await fetch(this.buildNextMatchupUrl(), {
          headers: {
            "Accept": "text/vnd.turbo-stream.html",
            "X-CSRF-Token": this.getCSRFToken()
          }
        })

        if (response.ok) {
          Turbo.renderStreamMessage(await response.text())
        }
      }

      // Wait one frame for Stimulus to connect targets, then transition to ready
      requestAnimationFrame(() => {
        this.state = "ready"
        this.lastKeyPressTime = Date.now()
        this.checkVideoMode()
        document.dispatchEvent(new CustomEvent("arena:content-updated"))
      })
    } catch (error) {
      console.error("Failed to load next matchup:", error)
      this.state = "ready"
      this.prefetchedResponse = null
      this.prefetchPromise = null
    }
  }

  // -- Prefetch next matchup during reveal --

  private async prefetchNextMatchup() {
    // Only prefetch during reveal, and only one at a time
    if (this.prefetchPromise || this.state !== "revealing") return

    const url = this.buildNextMatchupUrl()

    this.prefetchPromise = (async () => {
      try {
        const response = await fetch(url, {
          headers: {
            "Accept": "text/vnd.turbo-stream.html",
            "X-CSRF-Token": this.getCSRFToken()
          }
        })

        if (response.ok) {
          const responseText = await response.text()
          // Store original response for rendering into arena_content later
          this.prefetchedResponse = responseText

          // Render into hidden off-screen container to trigger browser image loading.
          // Only redirect the arena_content target; other targets (toasts, header)
          // render into their real locations immediately.
          const preloadHtml = responseText.replace(
            'target="arena_content"',
            'target="arena_prefetch"'
          )
          Turbo.renderStreamMessage(preloadHtml)
        }
      } catch {
        // Prefetch failure is non-critical — loadNextMatchup falls back to normal fetch
      }
    })()
  }

  private buildNextMatchupUrl(): string {
    const params = new URLSearchParams()
    if (this.featuringValue) {
      params.set("featuring", this.featuringValue)
    }
    if (this.challengeValue) {
      params.set("challenge", this.challengeValue)
    }
    if (this.battleValue) {
      params.set("battle_slugs", this.battleValue)
    }
    // Pass current competition ID to avoid showing same competition twice in a row
    if (this.hasCompetitionIdTarget && this.competitionIdTarget.value) {
      params.set("avoid_competition", this.competitionIdTarget.value)
    }

    const slug = this.competitionTypeSlugValue || "text-to-image"
    let url = `/arena/vote/${slug}/next`
    if (params.toString()) {
      url += `?${params.toString()}`
    }
    return url
  }

  getCSRFToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? (meta as HTMLMetaElement).content : ""
  }

  // Get the Turnstile response token for visitor verification
  private getTurnstileToken(): string | null {
    if (!this.visitorModeValue || !this.hasTurnstileContainerTarget) {
      return null
    }

    const responseInput = this.turnstileContainerTarget.querySelector<HTMLInputElement>(
      'input[name="cf-turnstile-response"]'
    )

    return responseInput?.value || null
  }

  // Smooth scroll to position the prompt box slightly below the top of viewport
  // Skip on mobile since the prompt is sticky there
  private scrollToPromptBox(): void {
    if (!this.hasPromptBoxTarget) return

    // Skip scrolling on mobile (< 768px) - prompt is sticky
    if (window.innerWidth < 768) return

    const promptBox = this.promptBoxTarget
    const offsetFromTop = 20 // pixels from top of viewport

    const targetScrollPosition = promptBox.getBoundingClientRect().top + window.scrollY - offsetFromTop

    window.scrollTo({
      top: targetScrollPosition,
      behavior: "smooth"
    })
  }

  // Play reveal videos after Turbo inserts them (autoplay doesn't fire on DOM insertion)
  private playRevealVideos(): void {
    this.revealVideoTargets.forEach(video => {
      video.load()
      video.play().catch(() => {})
    })
  }

  // -- Video mode: 5-second voting delay --

  // Detect if we're in video mode (either from a hidden input or the value)
  private isVideoMode(): boolean {
    if (this.videoModeValue) return true
    if (this.hasVideoModeTarget && this.videoModeTarget.value === "true") return true
    return false
  }

  // Called on connect and after loading next matchup to set up video voting delay
  private checkVideoMode(): void {
    if (!this.isVideoMode()) {
      this.votingEnabled = true
      return
    }

    // Disable voting initially for video mode
    this.votingEnabled = false
    this.setupVideoVotingDelay()
  }

  private setupVideoVotingDelay(): void {
    // Defensively cancel any orphan tick before wiring up a new countdown.
    this.stopVideoVotingCountdown()

    if (!this.hasLeftVideoTarget || !this.hasRightVideoTarget) {
      // No video targets yet (may not be rendered), enable voting
      this.votingEnabled = true
      return
    }

    const leftVideo = this.leftVideoTarget
    const rightVideo = this.rightVideoTarget

    // Keep the countdown badge hidden until both videos are actually playing
    // and we have a real number to show — otherwise the badge flashes "Watch"
    // for a fraction of a second before flipping to "5s".

    // Disable card interactions on the figure card, vote banner, and tie button.
    this.setSideVotingState("left", false)
    this.setSideVotingState("right", false)
    this.setSideVotingState("tie", false)

    let leftPlaying = false
    let rightPlaying = false
    let bothPlayingTime: number | null = null

    const checkBothPlaying = () => {
      if (leftPlaying && rightPlaying && bothPlayingTime === null) {
        bothPlayingTime = performance.now()
        this.startVideoVotingCountdown(bothPlayingTime)
      }
    }

    // Listen for "playing" events to know when each video actually starts
    leftVideo.addEventListener("playing", () => { leftPlaying = true; checkBothPlaying() }, { once: true })
    rightVideo.addEventListener("playing", () => { rightPlaying = true; checkBothPlaying() }, { once: true })

    // After Turbo swap, autoplay doesn't fire on inserted <video> elements.
    // We need to explicitly call .load() then .play() once both are ready.
    const tryPlay = () => {
      if (leftVideo.readyState >= 3 && rightVideo.readyState >= 3) {
        // Both buffered enough — start together
        leftVideo.play().catch(() => {})
        rightVideo.play().catch(() => {})
      }
    }

    leftVideo.addEventListener("canplay", tryPlay, { once: true })
    rightVideo.addEventListener("canplay", tryPlay, { once: true })

    // Kick off loading explicitly — required after Turbo DOM insertion
    leftVideo.load()
    rightVideo.load()

    // If already playing (e.g. initial page load where autoplay worked)
    if (!leftVideo.paused) { leftPlaying = true; checkBothPlaying() }
    if (!rightVideo.paused) { rightPlaying = true; checkBothPlaying() }

    // Fallback: if videos don't fire playing within 10s, enable voting anyway
    setTimeout(() => {
      if (!this.votingEnabled) {
        this.enableVideoVoting()
      }
    }, 10000)
  }

  private startVideoVotingCountdown(startTime: number): void {
    const delayMs = this.votingDelaySecondsValue * 1000

    // Reveal the badge with the initial value now that we have a real number,
    // skipping the "Watch" intermediate state.
    this.updateVotingCountdownText(`${this.votingDelaySecondsValue}s`)
    this.votingCountdownTargets.forEach(el => el.classList.remove("hidden"))

    const tick = (now: number) => {
      const elapsed = now - startTime
      const remaining = Math.ceil((delayMs - elapsed) / 1000)

      if (remaining <= 0) {
        this.enableVideoVoting()
        return
      }

      this.updateVotingCountdownText(`${remaining}s`)
      this.videoVotingRafId = requestAnimationFrame(tick)
    }

    this.videoVotingRafId = requestAnimationFrame(tick)
  }

  private stopVideoVotingCountdown(): void {
    if (this.videoVotingRafId) {
      cancelAnimationFrame(this.videoVotingRafId)
      this.videoVotingRafId = null
    }
  }

  private enableVideoVoting(): void {
    this.stopVideoVotingCountdown()
    this.votingEnabled = true

    // Hide countdown badges
    this.votingCountdownTargets.forEach(el => el.classList.add("hidden"))

    // Enable card interactions on the figure card, vote banner, and tie button.
    this.setSideVotingState("left", true)
    this.setSideVotingState("right", true)
    this.setSideVotingState("tie", true)
  }

  // Toggle the voting-enabled/disabled visual state for every element on a
  // given side (figure card + vote banner) in one shot. The split layout has
  // two siblings per side, so a single target.classList.add() isn't enough.
  // "tie" covers the centered tie button which is its own clickable target.
  private setSideVotingState(side: "left" | "right" | "tie", enabled: boolean): void {
    const els = this.matchupTarget.querySelectorAll<HTMLElement>(`[data-arena-side="${side}"]`)
    els.forEach(el => {
      el.classList.toggle("arena-card--voting-disabled", !enabled)
      el.classList.toggle("arena-card--voting-enabled", enabled)
    })
  }

  private updateVotingCountdownText(text: string): void {
    this.votingCountdownValueTargets.forEach(el => {
      el.textContent = text
    })
  }
}
